// ============================================================================
// Appunti / To Do / Link / Amministrazione — real FastAPI + MongoDB (Module D)
// ----------------------------------------------------------------------------
// Source of truth = backend `/api/notes` (internal users only, team-wide).
// A synchronous in-memory cache (hydrated once at auth bootstrap) keeps the
// existing UI working unchanged. Mutations are optimistic + persisted in the
// background. image_urls remain placeholder data URLs (no real upload).
// ============================================================================

import { api } from "@/lib/api";

export const SECTIONS = {
  todo: { label: "To Do", statusable: true },
  appunti: { label: "Appunti", statusable: false },
  link_utili: { label: "Link Utili", statusable: false },
  amministrazione: { label: "Amministrazione", statusable: true },
};

const now = () => new Date().toISOString();
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

// ---------------------------------------------------------------- cache layer
let _items = [];
let _cats = [];
let _hydrated = false;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeNotes = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isNotesReady = () => _hydrated;

export async function hydrateNotes() {
  try {
    const [items, cats] = await Promise.all([
      api.get("/notes/items").then((r) => r.data),
      api.get("/notes/categories").then((r) => r.data),
    ]);
    _items = Array.isArray(items) ? items : [];
    _cats = Array.isArray(cats) ? cats : [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("hydrateNotes failed", e?.response?.status);
  } finally {
    _hydrated = true;
    notify();
  }
}
export const resetNotes = () => { _items = []; _cats = []; _hydrated = false; };
const resync = () => { hydrateNotes(); };

// ---------------------------------------------------------------------------
// CATEGORIES
// ---------------------------------------------------------------------------
export const getCategories = (section) => _cats
  .filter((c) => c.section === section)
  .slice()
  .sort((a, b) => ((a.order ?? 1e6) - (b.order ?? 1e6)) || a.name.localeCompare(b.name));

export const addCategory = (section, name) => {
  const clean = (name || "").trim();
  if (!clean) return Promise.resolve(null);
  const existing = _cats.find((c) => c.section === section && c.name.toLowerCase() === clean.toLowerCase());
  if (existing) return Promise.resolve(existing.id);
  const tmp = uid("cat");
  _cats = [..._cats, { id: tmp, name: clean, section, created_at: now(), updated_at: now() }];
  notify();
  return api.post("/notes/categories", { section, name: clean })
    .then(({ data }) => {
      _cats = _cats.map((c) => (c.id === tmp ? data : c));
      // Migra eventuali elementi creati contro l'id temporaneo verso l'id reale del server.
      _items = _items.map((i) => (i.category_id === tmp ? { ...i, category_id: data.id } : i));
      notify();
      return data.id;
    })
    .catch(() => { resync(); return tmp; });
};

export const categoryName = (id) => _cats.find((c) => c.id === id)?.name || null;

export const renameCategory = (id, name) => {
  const clean = (name || "").trim();
  if (!clean) return;
  _cats = _cats.map((c) => (c.id === id ? { ...c, name: clean } : c));
  notify();
  api.patch(`/notes/categories/${id}`, { name: clean }).catch(resync);
};

export const reorderCategories = (section, ids) => {
  const order = new Map(ids.map((id, i) => [id, i]));
  _cats = [..._cats].sort((a, b) => {
    if (a.section !== section || b.section !== section) return 0;
    return (order.get(a.id) ?? 9999) - (order.get(b.id) ?? 9999);
  });
  notify();
  api.post("/notes/categories/reorder", { section, ids }).catch(resync);
};

export const removeCategory = (id) => {
  _cats = _cats.filter((c) => c.id !== id);
  // Gli elementi della cartella tornano "senza cartella" localmente.
  _items = _items.map((i) => (i.category_id === id ? { ...i, category_id: null } : i));
  notify();
  api.delete(`/notes/categories/${id}`).catch(resync);
};

// ---------------------------------------------------------------------------
// ITEMS
// ---------------------------------------------------------------------------
export const getItems = (section) =>
  _items.filter((i) => i.section === section).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

export const addItem = (section, data, author) => {
  const statusable = SECTIONS[section]?.statusable;
  const id = uid("note");
  const optimistic = {
    id, section, title: (data.title || "").trim() || "Senza titolo",
    category_id: data.category_id || null, content: data.content || "",
    url: data.url || "", status: statusable ? "todo" : "active",
    image_urls: data.image_urls || [], shared_with: data.shared_with || [],
    priority: data.priority || null, assigned_user_id: data.assigned_user_id || null,
    created_by: author || "Operatore", created_at: now(), updated_at: now(),
  };
  _items = [..._items, optimistic];
  notify();
  api.post("/notes/items", {
    id, section, title: optimistic.title, category_id: optimistic.category_id, content: optimistic.content,
    url: optimistic.url, image_urls: optimistic.image_urls, shared_with: optimistic.shared_with,
    priority: optimistic.priority, assigned_user_id: optimistic.assigned_user_id,
  }).then(({ data }) => { _items = _items.map((i) => (i.id === id ? data : i)); notify(); }).catch(resync);
  return id;
};

export const updateItem = (id, patch) => {
  const idx = _items.findIndex((i) => i.id === id);
  if (idx === -1) return;
  _items[idx] = { ..._items[idx], ...patch, updated_at: now() };
  _items = [..._items];
  notify();
  api.patch(`/notes/items/${id}`, patch).catch(resync);
};

export const removeItem = (id) => {
  _items = _items.filter((i) => i.id !== id);
  notify();
  api.delete(`/notes/items/${id}`).catch(resync);
};

// Ordine manuale (drag & drop) — usato per riordinare gli elementi chiusi/completati.
export const reorderItems = (section, ids) => {
  const order = new Map(ids.map((id, i) => [id, i]));
  _items = _items.map((i) => (i.section === section && order.has(i.id) ? { ...i, order: order.get(i.id) } : i));
  notify();
  api.post("/notes/items/reorder", { section, ids }).catch(resync);
};

export const toggleStatus = (id) => {
  const it = _items.find((i) => i.id === id);
  if (!it) return;
  const completing = it.status !== "completed";
  updateItem(id, { status: completing ? "completed" : "todo", completed_at: completing ? now() : null });
};

// --- Pianificazione To Do ↔ Calendario (single source of truth = To Do item) ---
export const TODO_PRIORITIES = { low: { label: "Bassa", tone: "neutral" }, medium: { label: "Media", tone: "info" }, high: { label: "Alta", tone: "warning" } };

export const getTodoItems = () => getItems("todo");
export const unscheduledTodos = () => getTodoItems().filter((i) => i.status !== "completed" && (!i.planned_start_time || i.show_in_calendar === false));
export const scheduledTodos = () => getTodoItems().filter((i) => i.planned_start_time && i.show_in_calendar !== false && i.status !== "completed");

export const planTodo = (id, { planned_start_time, planned_end_time, planned_duration_minutes = 60, assigned_user_id }) =>
  updateItem(id, { planned_start_time, planned_end_time, planned_duration_minutes, planned_date: planned_start_time, show_in_calendar: true, ...(assigned_user_id ? { assigned_user_id } : {}) });

export const unplanTodo = (id) =>
  updateItem(id, { planned_start_time: null, planned_end_time: null, planned_date: null, show_in_calendar: false });

export const completeTodo = (id) => updateItem(id, { status: "completed", completed_at: now() });

// Elementi (appunti/link) condivisi con un membro del team.
export const getSharedWithUser = (userId) => {
  if (!userId) return [];
  return _items
    .filter((i) => (i.section === "appunti" || i.section === "link_utili") && (i.shared_with || []).includes(userId))
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
};
